import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Checkbox, CircularProgress, TextField, InputAdornment, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    ArrowLeft, ArrowRight, Eye, ArrowDownWideNarrow, ArrowUpNarrowWide, Trash2,
    Search, Dot, Users, CalendarDays, CalendarRange, ChevronDown, Check,
} from 'lucide-react';
import Calling from '../../api/Calling';
import formatDate from '../../components/formatDate';
import generateCaptcha from '../../components/captcha';
import getISTDate from '../../components/getISTDate';
import User_Navbar from './User_Navbar';
import MainButton from '../../components/ul/MainButton';
import ActionButton from '../../components/ul/ActionButton';
import DeleteButton from '../../components/ul/DeleteButton';
import NotFound from '../../components/ul/NotFound';
import TableSkeleton from '../../components/ul/TableSkeleton';

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup (keys verified for @mui/x-date-pickers@9.6).
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: { color: '#374151', '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: { color: '#6b7280', borderRadius: 10, '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: { fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase' },
            },
        },
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
    },
});

const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];
const checkboxSx = {
    color: '#9ca3af',
    p: 0.5,
    '&.Mui-checked': { color: BRAND },
    '&.MuiCheckbox-indeterminate': { color: BRAND },
};

const User = () => {
    const [loader, setLoader] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState(-1);
    const [selectAll, setSelectAll] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));
    const [totalUser, setTotalUser] = useState(0);
    const [blockUser, setBloackUser] = useState(false);
    const [selectState, setSelectState] = useState('today');
    const limit = 200;

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getAllUserData(currentPage, startDate, endDate, limit, search, sortOrder, blockUser);
            if (response?.status) {
                setData(response?.data);
                setTotalPages(response?.totalPage);
                setTotalUser(response?.totalRecord);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortOrder, startDate, endDate, blockUser, selectState]);

    // Keep the picker drafts in sync whenever the committed range changes (quick buttons / apply)
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    const handleSelectAll = () => {
        if (!selectAll) {
            const allUserIds = data.map((user) => user._id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
        setSelectAll(!selectAll);
    };

    const handleSelectUser = (id) => {
        if (selectedUsers.includes(id)) {
            setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
        } else {
            setSelectedUsers([...selectedUsers, id]);
        }
    };

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
        setCurrentPage(1);
    };

    // Commit the picked draft range — this is the ONLY thing that applies a custom range
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectState('range');
        setCurrentPage(1);
    };

    const handleDeleteSelected = async (id) => {
        const content = `You are about to delete a user. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);
        if (check) {
            try {
                setLoader(true);
                let body = {};
                if (id) {
                    body.id = [id];
                } else {
                    body.id = selectedUsers;
                }
                const response = await Calling.deleteUser(body);
                if (response?.status) {
                    toast.success(response?.message);
                    setSelectedUsers([]);
                    setSelectAll(false);
                    fetchData();
                } else {
                    toast.error(response?.message);
                }
            } catch (error) {
                console.error('Error deleting users:', error);
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    const handleEdit = (id) => {
        window.open(`/manage/user/all/info/${id}`, '_blank');
    };

    const handleBlockUser = async (id, status) => {
        setLoader(true);
        try {
            const response = await Calling.blockUser(id, status);
            if (response?.status) {
                toast.success(response?.message);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error blocking user:', error);
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    const handleReportSort = () => {
        setSelectState('sort')
        if (sortOrder === 1) {
            setSortOrder(-1)
        } else {
            setSortOrder(1)
        }
    }

    const handleBlockFiletr = () => {
        setBloackUser(!blockUser)
        setSelectState('block')
    }

    const handleSearchUser = (e) => {
        setCurrentPage(1);
        setSearch(e.target.value)
        setSelectState('search')
    }

    useEffect(() => {
        if (search.trim() !== '') {
            const delayDebounce = setTimeout(() => {
                fetchData();
            }, 500);

            return () => clearTimeout(delayDebounce);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full min-h-screen text-main-font"
        >
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                        <Users size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">User Management</h1>
                        <p className="text-sm text-main-subfont">Manage and monitor all users</p>
                    </div>
                </div>
            </div>

            {/* Main Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Navigation */}
                <User_Navbar selectState={'user'} />
                <hr className="border-main-border" />

                {/* Total Users Card */}
                <div className="m-4">
                    <div className="p-5 bg-main-th rounded-xl border border-main-blue/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-main-subfont">Total Users Created</p>
                                <p className="text-2xl font-bold text-main-font mt-1">
                                    {loader ? (
                                        <span className="flex items-center text-main-subfont text-base">
                                            <CircularProgress size={18} sx={{ color: 'inherit', mr: 1 }} />
                                            Loading...
                                        </span>
                                    ) : (
                                        totalUser.toLocaleString()
                                    )}
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-main-blue/10">
                                <Users size={28} className="text-main-blue" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Date Range Filters */}
                <div className="px-4 py-4 border-t border-main-border">
                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectState === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            type="button"
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: ((draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') || (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '')) ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectState === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-main-th border border-main-blue/20 rounded-lg">
                            <CalendarRange size={14} className="text-main-blue" />
                            <span className="text-xs text-main-subfont">
                                Showing <span className="font-semibold text-main-font">{formatDate(startDate)}</span> → <span className="font-semibold text-main-font">{formatDate(endDate)}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* Action Buttons and Search */}
                <div className="px-4 py-3 border-t border-main-border">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                            <MainButton
                                onClick={handleBlockFiletr}
                                selectState={selectState === 'block'}
                                label={blockUser ? 'Unblock' : 'Block'}
                            />
                            <MainButton
                                onClick={handleReportSort}
                                label={
                                    <span className="flex items-center gap-2">
                                        {sortOrder === -1 ? <ArrowDownWideNarrow size={16} /> : <ArrowUpNarrowWide size={16} />}
                                        Sort
                                    </span>
                                }
                                selectState={selectState === 'sort'}
                            />
                        </div>

                        {/* Search Input */}
                        <div className="w-full lg:w-80">
                            <TextField
                                fullWidth
                                size="small"
                                type="search"
                                value={search}
                                onChange={handleSearchUser}
                                placeholder="Search users..."
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search size={18} className="text-main-subfont" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-main-th">
                                <th className="ps-4 py-4 text-left">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            size="small"
                                            sx={checkboxSx}
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                        />
                                        {selectedUsers.length > 0 && (
                                            <DeleteButton
                                                onClick={() => handleDeleteSelected(null)}
                                                label={<Trash2 size={18} />}
                                            />
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">#</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">User</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Referral</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Plan</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Device</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Status</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Date</th>
                                <th className="px-4 py-4 text-left text-xs font-semibold text-main-subfont uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>

                        {loader ? (
                            <tbody>
                                <tr>
                                    <td colSpan={9} className="p-0">
                                        <TableSkeleton rows={8} cols={9} />
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody className="divide-y divide-main-border">
                                {data.length > 0 ? data.map((user, index) => (
                                    <tr key={user._id} className="hover:bg-main-thHover transition-colors">
                                        <td className="ps-4 py-4">
                                            <Checkbox
                                                size="small"
                                                sx={checkboxSx}
                                                checked={selectedUsers.includes(user._id)}
                                                onChange={() => handleSelectUser(user._id)}
                                            />
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{index + 1 + (currentPage - 1) * limit}</td>
                                        <td className="px-4 py-4">
                                            <div
                                                className="flex items-center gap-3 cursor-pointer"
                                                onClick={() => handleEdit(user?._id)}
                                            >
                                                {user?.u_profile ? (
                                                    <img src={user.u_profile} alt={user.u_name} className="w-10 h-10 rounded-xl object-cover border border-main-border" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-xl bg-main-blue text-main-white flex items-center justify-center font-bold text-lg">
                                                        {user?.u_name?.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-main-font">{user.u_name}</p>
                                                    <p className="text-xs text-main-subfont">{user.u_email || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="font-semibold text-main-font">{user.u_ref_count || 0}</span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg ${user.has_active_plan
                                                ? 'bg-main-th text-main-blue border border-main-blue/30'
                                                : 'bg-main-bg text-main-subfont border border-main-border'
                                                }`}>
                                                {user.has_active_plan ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont cursor-pointer" onClick={() => handleEdit(user?._id)}>
                                            {user.u_device || 'N/A'}
                                        </td>
                                        <td className="px-4 py-4">
                                            {user.u_status ? (
                                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-main-th text-main-blue border border-main-blue/30">
                                                    <Dot size={18} /> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-main-delete/10 text-main-delete border border-main-delete/30">
                                                    <Dot size={18} /> Blocked
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont">{formatDate(user.createdAt)}</td>
                                        <td className="px-4 py-4">
                                            <ActionButtons
                                                v={user}
                                                handleDeleteSelected={handleDeleteSelected}
                                                handleEdit={handleEdit}
                                                handleBlockUser={handleBlockUser}
                                            />
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={9} className="py-12">
                                            <NotFound />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        )}
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-6 border-t border-main-border">
                    <div className="flex flex-col md:flex-row justify-center items-center relative">
                        <div className="flex gap-3 mb-4 md:mb-0">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === 1
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                <ArrowLeft size={12} /> Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${currentPage === totalPages
                                    ? "bg-main-bg border border-main-border text-main-subfont cursor-not-allowed"
                                    : "bg-main-blue text-main-white hover:bg-main-blue/90"
                                    }`}
                            >
                                Next <ArrowRight size={12} />
                            </button>
                        </div>

                        <p className="text-main-subfont md:absolute md:right-0 text-sm">
                            Page{' '}
                            <span className="px-3 py-1.5 bg-main-th border border-main-border text-main-font font-medium rounded-lg">
                                {currentPage}
                            </span>
                            {' '}of{' '}
                            <span className="font-medium text-main-font">{totalPages}</span>
                        </p>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default User;

const ActionButtons = ({ v, handleDeleteSelected, handleEdit, handleBlockUser }) => {
    const [showActions, setShowActions] = useState(false);

    const handleButtonClick = () => {
        setShowActions(!showActions);
    };

    return (
        <div className="relative inline-block">
            <ActionButton onClick={handleButtonClick} />

            <AnimatePresence>
                {showActions && (
                    <motion.div
                        key="user-actions-menu"
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-44 bg-main-white border border-main-border rounded-xl shadow-lg z-10 overflow-hidden"
                    >
                        {/* Block Toggle */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-main-border">
                            <span className="text-sm text-main-font">Block User</span>
                            <label className="cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={v?.u_status}
                                    onChange={() => handleBlockUser(v?._id, !v?.u_status)}
                                    className="sr-only peer"
                                    name="user_status"
                                />
                                <div className="relative w-10 h-5 bg-main-border rounded-full peer peer-checked:bg-main-delete after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-main-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                            </label>
                        </div>

                        {/* View Button */}
                        <button
                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-main-blue hover:bg-main-th transition-colors"
                            onClick={() => handleEdit(v?._id)}
                        >
                            <Eye size={16} /> View Details
                        </button>

                        {/* Delete Button */}
                        <button
                            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-main-delete hover:bg-main-delete/5 transition-colors"
                            onClick={() => handleDeleteSelected(v?._id)}
                        >
                            <Trash2 size={14} /> Delete User
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
